"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Eye, Users, Clock, Wallet, Star, Calendar, CheckCircle2, FileText, Download, UserPlus, Play, CheckCircle, XCircle } from "lucide-react";
import { DataTable, type Column } from "../DataTable";
import { SimpleTabs } from "../SimpleTabs";
import { PageHeader, StatCard } from "../ModuleHelpers";
import { useERP, formatBDT, formatNum, type Employee } from "@/lib/erp/store";
import { CSVExportButton, PrintButton } from "../PrintExport";
import { useToast } from "@/hooks/use-toast";

// ===== Types for additional HR entities =====
interface AttendanceRecord {
  id: string; empCode: string; name: string; date: string;
  checkIn: string; checkOut: string; status: "Present" | "On Field" | "On Trip" | "Absent" | "On Leave";
  overtime: string; late: string;
}
interface PayrollRecord {
  id: string; runId: string; month: string; empCode: string; name: string;
  gross: number; tax: number; pf: number; net: number; status: "Paid" | "Pending";
}
interface AppraisalRecord {
  id: string; empCode: string; name: string; period: string;
  rating: "A+" | "A" | "B+" | "B" | "C" | "Pending"; score: number;
  reviewer: string; status: "Completed" | "In Progress"; goals: string;
}
interface LeaveRecord {
  id: string; empCode: string; name: string; type: "Casual" | "Sick" | "Earned" | "Without Pay";
  fromDate: string; toDate: string; days: number;
  status: "Approved" | "Pending" | "Rejected"; reason: string;
}

// ===== Seed data for HR =====
const INITIAL_ATTENDANCE: AttendanceRecord[] = [
  { id: "att1", empCode: "EMP-1024", name: "Arif Rahman", date: "2026-10-13", checkIn: "09:02", checkOut: "18:15", status: "Present", overtime: "0h", late: "0m" },
  { id: "att2", empCode: "EMP-1018", name: "Sadia Islam", date: "2026-10-13", checkIn: "09:15", checkOut: "18:30", status: "Present", overtime: "0h", late: "15m" },
  { id: "att3", empCode: "EMP-1008", name: "Imran Khan", date: "2026-10-13", checkIn: "—", checkOut: "—", status: "On Field", overtime: "—", late: "—" },
  { id: "att4", empCode: "EMP-1004", name: "Rumana Begum", date: "2026-10-13", checkIn: "08:55", checkOut: "18:00", status: "Present", overtime: "0h", late: "0m" },
  { id: "att5", empCode: "EMP-0998", name: "Md. Hasan", date: "2026-10-13", checkIn: "08:48", checkOut: "18:20", status: "Present", overtime: "0h", late: "0m" },
  { id: "att6", empCode: "EMP-0992", name: "Rakib Hossain", date: "2026-10-13", checkIn: "—", checkOut: "—", status: "On Trip", overtime: "—", late: "—" },
];

const INITIAL_PAYROLL: PayrollRecord[] = [
  { id: "pr1", runId: "PR-2026-10", month: "October 2026", empCode: "EMP-1024", name: "Arif Rahman", gross: 250000, tax: 25000, pf: 12500, net: 212500, status: "Paid" },
  { id: "pr2", runId: "PR-2026-10", month: "October 2026", empCode: "EMP-1018", name: "Sadia Islam", gross: 180000, tax: 18000, pf: 9000, net: 153000, status: "Paid" },
  { id: "pr3", runId: "PR-2026-10", month: "October 2026", empCode: "EMP-1008", name: "Imran Khan", gross: 120000, tax: 12000, pf: 6000, net: 102000, status: "Pending" },
  { id: "pr4", runId: "PR-2026-10", month: "October 2026", empCode: "EMP-1004", name: "Rumana Begum", gross: 150000, tax: 15000, pf: 7500, net: 127500, status: "Paid" },
  { id: "pr5", runId: "PR-2026-10", month: "October 2026", empCode: "EMP-0998", name: "Md. Hasan", gross: 95000, tax: 9500, pf: 4750, net: 80750, status: "Pending" },
  { id: "pr6", runId: "PR-2026-10", month: "October 2026", empCode: "EMP-0992", name: "Rakib Hossain", gross: 75000, tax: 7500, pf: 3750, net: 63750, status: "Pending" },
];

const INITIAL_APPRAISALS: AppraisalRecord[] = [
  { id: "ap1", empCode: "EMP-1024", name: "Arif Rahman", period: "Jan-Jun 2026", rating: "A+", score: 92, reviewer: "Board", status: "Completed", goals: "Revenue target achieved 110%" },
  { id: "ap2", empCode: "EMP-1018", name: "Sadia Islam", period: "Jan-Jun 2026", rating: "A", score: 85, reviewer: "CEO", status: "Completed", goals: "Sales growth +18%" },
  { id: "ap3", empCode: "EMP-1008", name: "Imran Khan", period: "Jan-Jun 2026", rating: "B+", score: 78, reviewer: "Head of Sales", status: "Completed", goals: "Outlet coverage 89%" },
  { id: "ap4", empCode: "EMP-1004", name: "Rumana Begum", period: "Jan-Jun 2026", rating: "A", score: 88, reviewer: "CFO", status: "Completed", goals: "Period closing on time" },
  { id: "ap5", empCode: "EMP-0998", name: "Md. Hasan", period: "Jul-Dec 2026", rating: "Pending", score: 0, reviewer: "Operations Head", status: "In Progress", goals: "Stock accuracy 98%+" },
];

const INITIAL_LEAVE: LeaveRecord[] = [
  { id: "lv1", empCode: "EMP-1024", name: "Arif Rahman", type: "Casual", fromDate: "2026-10-20", toDate: "2026-10-20", days: 1, status: "Approved", reason: "Personal work" },
  { id: "lv2", empCode: "EMP-1008", name: "Imran Khan", type: "Sick", fromDate: "2026-10-15", toDate: "2026-10-16", days: 2, status: "Approved", reason: "Fever" },
  { id: "lv3", empCode: "EMP-0992", name: "Rakib Hossain", type: "Earned", fromDate: "2026-11-01", toDate: "2026-11-05", days: 5, status: "Pending", reason: "Family vacation" },
];

// Leave balance tracker
const LEAVE_BALANCES = [
  { type: "Casual Leave" as const, used: 3, total: 10, color: "#06b6d4" },
  { type: "Sick Leave" as const, used: 2, total: 15, color: "#f59e0b" },
  { type: "Earned Leave" as const, used: 5, total: 20, color: "#10b981" },
  { type: "Without Pay" as const, used: 0, total: 0, color: "#ef4444" },
];

const DEPARTMENTS = ["Sales", "Finance", "Operations", "HR", "Logistics", "IT", "Marketing", "Procurement"];
const DESIGNATIONS = ["CEO", "CFO", "Manager", "Officer", "Executive", "Assistant", "Head", "Coordinator"];

export function HRModule({ pageId, onNavigate }: { pageId: string; onNavigate?: (id: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [tab, setTab] = React.useState("employees");

  // Live state for HR sub-entities
  const [attendance, setAttendance] = React.useState<AttendanceRecord[]>(INITIAL_ATTENDANCE);
  const [payroll, setPayroll] = React.useState<PayrollRecord[]>(INITIAL_PAYROLL);
  const [appraisals, setAppraisals] = React.useState<AppraisalRecord[]>(INITIAL_APPRAISALS);
  const [leave, setLeave] = React.useState<LeaveRecord[]>(INITIAL_LEAVE);

  // Dialog state
  const [empDialogOpen, setEmpDialogOpen] = React.useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = React.useState(false);
  const [appraisalDialogOpen, setAppraisalDialogOpen] = React.useState(false);

  // Determine initial tab based on pageId
  React.useEffect(() => {
    if (pageId.includes("attendance")) setTab("attendance");
    else if (pageId.includes("payroll")) setTab("payroll");
    else if (pageId.includes("payslip")) setTab("payslip");
    else if (pageId.includes("appraisal")) setTab("appraisal");
    else if (pageId.includes("leave") || pageId.includes("kpi")) setTab("leave");
    else setTab("employees");
  }, [pageId]);

  const employees = erp.employees;

  // ===== Actions =====
  const handleAddEmployee = (data: Omit<Employee, "id">) => {
    erp.addEmployee(data);
    toast({
      title: "Employee Added",
      description: `${data.name} (${data.empCode}) has been added successfully.`,
    });
    setEmpDialogOpen(false);
  };

  const handleApplyLeave = (data: Omit<LeaveRecord, "id" | "status">) => {
    const newLeave: LeaveRecord = { ...data, id: `lv${Date.now()}`, status: "Pending" };
    setLeave(prev => [newLeave, ...prev]);
    toast({
      title: "Leave Application Submitted",
      description: `${data.days} day(s) of ${data.type} leave requested by ${data.name}.`,
    });
    setLeaveDialogOpen(false);
  };

  const handleRunPayroll = () => {
    const pending = payroll.filter(p => p.status === "Pending");
    if (pending.length === 0) {
      toast({ title: "No Pending Payroll", description: "All payroll entries are already paid." });
      return;
    }
    setPayroll(prev => prev.map(p => p.status === "Pending" ? { ...p, status: "Paid" } : p));
    toast({
      title: "Payroll Processed",
      description: `${pending.length} payroll entries marked as Paid. Total disbursed: ${formatBDT(pending.reduce((s, p) => s + p.net, 0))}`,
    });
  };

  const handleApproveLeave = (id: string) => {
    setLeave(prev => prev.map(l => l.id === id ? { ...l, status: "Approved" } : l));
    const rec = leave.find(l => l.id === id);
    toast({ title: "Leave Approved", description: `${rec?.name}'s ${rec?.type} leave approved.` });
  };

  const handleRejectLeave = (id: string) => {
    setLeave(prev => prev.map(l => l.id === id ? { ...l, status: "Rejected" } : l));
    const rec = leave.find(l => l.id === id);
    toast({ title: "Leave Rejected", description: `${rec?.name}'s leave request rejected.`, variant: "destructive" });
  };

  const handleMarkAllPresent = () => {
    const today = new Date().toISOString().slice(0, 10);
    setAttendance(prev => prev.map(a => a.status === "On Field" || a.status === "On Trip" ? a : {
      ...a, status: "Present", checkIn: a.checkIn === "—" ? "09:00" : a.checkIn, checkOut: a.checkOut === "—" ? "18:00" : a.checkOut, date: today,
    }));
    toast({ title: "Attendance Updated", description: "All office employees marked present for today." });
  };

  const empColumns: Column<Employee>[] = [
    { key: "empCode", header: "Emp Code", sortable: true, render: e => <span className="font-mono text-xs">{e.empCode}</span> },
    { key: "name", header: "Name", sortable: true, render: e => <span className="font-medium">{e.name}</span> },
    { key: "department", header: "Department", sortable: true },
    { key: "designation", header: "Designation" },
    { key: "phone", header: "Phone", render: e => <span className="text-xs">{e.phone}</span> },
    { key: "status", header: "Status", render: e => <Badge variant={e.status === "Active" ? "default" : e.status === "On Field" ? "secondary" : "outline"}>{e.status}</Badge> },
    { key: "actions", header: "Actions", align: "right", render: e => (
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" className="size-7 p-0" onClick={() => toast({ title: e.name, description: `${e.designation} · ${e.department} · ${e.phone}` })}><Eye className="size-3.5" /></Button>
        <Button size="sm" variant="ghost" className="size-7 p-0" onClick={() => toast({ title: "Edit Employee", description: `Editing ${e.name} (coming up next)` })}><Pencil className="size-3.5" /></Button>
      </div>
    )},
  ];

  const attColumns: Column<AttendanceRecord>[] = [
    { key: "empCode", header: "Emp Code", sortable: true, render: a => <span className="font-mono text-xs">{a.empCode}</span> },
    { key: "name", header: "Name", sortable: true, render: a => <span className="font-medium">{a.name}</span> },
    { key: "date", header: "Date", sortable: true },
    { key: "checkIn", header: "Check In", align: "center" },
    { key: "checkOut", header: "Check Out", align: "center" },
    { key: "late", header: "Late", align: "right", render: a => <span className={a.late !== "0m" && a.late !== "—" ? "text-amber-600 font-medium" : ""}>{a.late}</span> },
    { key: "overtime", header: "Overtime", align: "right" },
    { key: "status", header: "Status", render: a => <Badge variant={a.status === "Present" ? "default" : a.status === "Absent" ? "destructive" : "secondary"}>{a.status}</Badge> },
  ];

  const payrollColumns: Column<PayrollRecord>[] = [
    { key: "empCode", header: "Emp Code", sortable: true, render: p => <span className="font-mono text-xs">{p.empCode}</span> },
    { key: "name", header: "Name", sortable: true, render: p => <span className="font-medium">{p.name}</span> },
    { key: "gross", header: "Gross", align: "right", sortable: true, render: p => formatBDT(p.gross) },
    { key: "tax", header: "Tax", align: "right", render: p => <span className="text-rose-600">-{formatBDT(p.tax)}</span> },
    { key: "pf", header: "PF", align: "right", render: p => <span className="text-amber-600">-{formatBDT(p.pf)}</span> },
    { key: "net", header: "Net Pay", align: "right", sortable: true, render: p => <span className="font-semibold text-emerald-600">{formatBDT(p.net)}</span> },
    { key: "status", header: "Status", render: p => <Badge variant={p.status === "Paid" ? "default" : "secondary"}>{p.status}</Badge> },
  ];

  const appraisalColumns: Column<AppraisalRecord>[] = [
    { key: "empCode", header: "Emp Code", sortable: true, render: a => <span className="font-mono text-xs">{a.empCode}</span> },
    { key: "name", header: "Name", sortable: true, render: a => <span className="font-medium">{a.name}</span> },
    { key: "period", header: "Period" },
    { key: "rating", header: "Rating", align: "center", render: a => <Badge variant={a.rating === "A+" ? "default" : a.rating === "A" ? "secondary" : a.rating === "Pending" ? "outline" : "outline"}>{a.rating}</Badge> },
    { key: "score", header: "Score", align: "right", render: a => a.score > 0 ? `${a.score}/100` : "—" },
    { key: "reviewer", header: "Reviewer" },
    { key: "goals", header: "Goals", render: a => <span className="text-xs text-muted-foreground">{a.goals}</span> },
    { key: "status", header: "Status", render: a => <Badge variant={a.status === "Completed" ? "default" : "secondary"}>{a.status}</Badge> },
  ];

  const leaveColumns: Column<LeaveRecord>[] = [
    { key: "empCode", header: "Emp Code", sortable: true, render: l => <span className="font-mono text-xs">{l.empCode}</span> },
    { key: "name", header: "Name", sortable: true, render: l => <span className="font-medium">{l.name}</span> },
    { key: "type", header: "Leave Type", render: l => <Badge variant="outline">{l.type}</Badge> },
    { key: "fromDate", header: "From" },
    { key: "toDate", header: "To" },
    { key: "days", header: "Days", align: "right" },
    { key: "reason", header: "Reason", render: l => <span className="text-xs">{l.reason}</span> },
    { key: "status", header: "Status", render: l => <Badge variant={l.status === "Approved" ? "default" : l.status === "Rejected" ? "destructive" : "secondary"}>{l.status}</Badge> },
    { key: "actions", header: "Actions", align: "right", render: l => l.status === "Pending" ? (
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" className="size-7 p-0 text-emerald-600" onClick={() => handleApproveLeave(l.id)}><CheckCircle className="size-3.5" /></Button>
        <Button size="sm" variant="ghost" className="size-7 p-0 text-rose-600" onClick={() => handleRejectLeave(l.id)}><XCircle className="size-3.5" /></Button>
      </div>
    ) : <span className="text-xs text-muted-foreground">—</span> },
  ];

  const totalPayroll = payroll.reduce((s, p) => s + p.net, 0);
  const presentToday = attendance.filter(a => a.status === "Present").length;
  const pendingPayroll = payroll.filter(p => p.status === "Pending").length;
  const pendingLeave = leave.filter(l => l.status === "Pending").length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="HR & Payroll Management" desc="Employee management, attendance, payroll, appraisal & leave" icon={Users} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Employees" value={formatNum(employees.length)} icon={Users} accent="#7c3aed" />
        <StatCard label="Present Today" value={formatNum(presentToday)} icon={CheckCircle2} accent="#10b981" />
        <StatCard label="Monthly Payroll" value={formatBDT(totalPayroll)} icon={Wallet} accent="#f59e0b" />
        <StatCard label="Pending Payroll" value={formatNum(pendingPayroll)} icon={Clock} accent="#e11d48" tone="text-destructive" />
      </div>

      <SimpleTabs value={tab} onValueChange={setTab} tabs={[
        { value: "employees", label: "Employees" },
        { value: "attendance", label: "Attendance" },
        { value: "payroll", label: "Payroll" },
        { value: "payslip", label: "Payslips" },
        { value: "appraisal", label: "Appraisals" },
        { value: "leave", label: "Leave" },
      ]} tabsListClassName="grid w-full grid-cols-3 sm:grid-cols-6 mb-4" />

      {tab === "employees" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Employee Directory ({employees.length})</CardTitle>
            <div className="flex gap-2">
              <CSVExportButton columns={[{key:"empCode",label:"Code"},{key:"name",label:"Name"},{key:"department",label:"Dept"},{key:"designation",label:"Designation"},{key:"phone",label:"Phone"}]} rows={employees} filename="employees.csv" />
              <Button size="sm" onClick={() => setEmpDialogOpen(true)}><UserPlus className="size-4 mr-1" /> New Employee</Button>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable columns={empColumns} data={employees} searchKeys={["empCode","name","department"]} rowKey={e => e.id} />
          </CardContent>
        </Card>
      )}

      {tab === "attendance" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Daily Attendance — {new Date().toLocaleDateString("en-GB")}</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleMarkAllPresent}><CheckCircle2 className="size-4 mr-1" /> Mark All Present</Button>
              <Button size="sm" variant="outline"><Calendar className="size-4 mr-1" /> Select Date</Button>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable columns={attColumns} data={attendance} searchKeys={["empCode","name"]} rowKey={a => a.id} />
          </CardContent>
        </Card>
      )}

      {tab === "payroll" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Payroll — October 2026</CardTitle>
            <div className="flex gap-2">
              <CSVExportButton columns={[{key:"empCode",label:"Code"},{key:"name",label:"Name"},{key:"gross",label:"Gross"},{key:"tax",label:"Tax"},{key:"pf",label:"PF"},{key:"net",label:"Net"}]} rows={payroll} filename="payroll-oct-2026.csv" />
              <Button size="sm" onClick={handleRunPayroll}><Play className="size-4 mr-1" /> Run Payroll ({pendingPayroll} pending)</Button>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable columns={payrollColumns} data={payroll} searchKeys={["empCode","name"]} rowKey={p => p.id} />
            <div className="mt-4 flex justify-end gap-6 border-t pt-4">
              <div className="text-right"><div className="text-xs text-muted-foreground">Total Gross</div><div className="font-semibold">{formatBDT(payroll.reduce((s,p)=>s+p.gross,0))}</div></div>
              <div className="text-right"><div className="text-xs text-muted-foreground">Total Tax</div><div className="font-semibold text-rose-600">{formatBDT(payroll.reduce((s,p)=>s+p.tax,0))}</div></div>
              <div className="text-right"><div className="text-xs text-muted-foreground">Total Net</div><div className="font-semibold text-emerald-600">{formatBDT(totalPayroll)}</div></div>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "payslip" && (
        <Card>
          <CardHeader><CardTitle>Payslip Generation</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {payroll.map(p => (
                <div key={p.id} className="rounded-lg border p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{p.name}</span>
                    <Badge variant={p.status === "Paid" ? "default" : "secondary"}>{p.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{p.empCode} · {p.month}</div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Gross:</span><span>{formatBDT(p.gross)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Deductions:</span><span className="text-rose-600">-{formatBDT(p.tax + p.pf)}</span></div>
                  <div className="flex justify-between text-sm font-bold border-t pt-1"><span>Net Pay:</span><span className="text-emerald-600">{formatBDT(p.net)}</span></div>
                  <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => toast({ title: "Payslip Downloaded", description: `${p.name} — ${p.month} — Net ${formatBDT(p.net)}` })}><Download className="size-3.5 mr-1" /> Download Payslip</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "appraisal" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Performance Appraisals</CardTitle>
            <Button size="sm" onClick={() => setAppraisalDialogOpen(true)}><Star className="size-4 mr-1" /> New Appraisal</Button>
          </CardHeader>
          <CardContent>
            <DataTable columns={appraisalColumns} data={appraisals} searchKeys={["empCode","name"]} rowKey={a => a.id} />
          </CardContent>
        </Card>
      )}

      {tab === "leave" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Leave Management {pendingLeave > 0 && <Badge variant="secondary" className="ml-2">{pendingLeave} pending</Badge>}</CardTitle>
            <Button size="sm" onClick={() => setLeaveDialogOpen(true)}><Plus className="size-4 mr-1" /> Apply Leave</Button>
          </CardHeader>
          <CardContent>
            <DataTable columns={leaveColumns} data={leave} searchKeys={["empCode","name"]} rowKey={l => l.id} />
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {LEAVE_BALANCES.map(l => (
                <div key={l.type} className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">{l.type}</div>
                  <div className="text-lg font-semibold">{l.used} / {l.total}</div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-1">
                    <div className="h-full rounded-full" style={{ width: `${l.total > 0 ? (l.used / l.total) * 100 : 0}%`, background: l.color }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* === Add Employee Dialog === */}
      <AddEmployeeDialog
        open={empDialogOpen}
        onOpenChange={setEmpDialogOpen}
        onSubmit={handleAddEmployee}
        nextEmpCode={`EMP-${String(1024 + employees.length).padStart(4, "0")}`}
      />

      {/* === Apply Leave Dialog === */}
      <ApplyLeaveDialog
        open={leaveDialogOpen}
        onOpenChange={setLeaveDialogOpen}
        employees={employees}
        onSubmit={handleApplyLeave}
      />

      {/* === New Appraisal Dialog === */}
      <NewAppraisalDialog
        open={appraisalDialogOpen}
        onOpenChange={setAppraisalDialogOpen}
        employees={employees}
        onSubmit={(data) => {
          setAppraisals(prev => [{ ...data, id: `ap${Date.now()}`, status: "In Progress" }, ...prev]);
          toast({ title: "Appraisal Created", description: `${data.name}'s appraisal for ${data.period} is now In Progress.` });
          setAppraisalDialogOpen(false);
        }}
      />
    </div>
  );
}

// ===== Add Employee Dialog =====
function AddEmployeeDialog({
  open, onOpenChange, onSubmit, nextEmpCode,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (data: Omit<Employee, "id">) => void;
  nextEmpCode: string;
}) {
  const [name, setName] = React.useState("");
  const [department, setDepartment] = React.useState(DEPARTMENTS[0]);
  const [designation, setDesignation] = React.useState(DESIGNATIONS[2]);
  const [phone, setPhone] = React.useState("");
  const [status, setStatus] = React.useState<Employee["status"]>("Active");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    onSubmit({ empCode: nextEmpCode, name: name.trim(), department, designation, phone: phone.trim(), status });
    setName(""); setPhone("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Employee</DialogTitle>
          <DialogDescription>Create a new employee record. All fields are required.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="emp-code">Employee Code</Label>
              <Input id="emp-code" value={nextEmpCode} disabled className="font-mono" />
            </div>
            <div>
              <Label htmlFor="emp-name">Full Name *</Label>
              <Input id="emp-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Karim Uddin" required autoFocus />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Department</Label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Designation</Label>
              <Select value={designation} onValueChange={setDesignation}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{DESIGNATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="emp-phone">Phone *</Label>
              <Input id="emp-phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+880 1XXXXXXXXX" required />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as Employee["status"])}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="On Leave">On Leave</SelectItem>
                  <SelectItem value="On Field">On Field</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit"><UserPlus className="size-4 mr-1" /> Add Employee</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===== Apply Leave Dialog =====
function ApplyLeaveDialog({
  open, onOpenChange, employees, onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employees: Employee[];
  onSubmit: (data: Omit<LeaveRecord, "id" | "status">) => void;
}) {
  const [empId, setEmpId] = React.useState(employees[0]?.id ?? "");
  const [type, setType] = React.useState<LeaveRecord["type"]>("Casual");
  const [fromDate, setFromDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = React.useState("");

  const selectedEmp = employees.find(e => e.id === empId);
  const days = fromDate && toDate ? Math.max(1, Math.round((new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000) + 1) : 1;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmp || !reason.trim()) return;
    onSubmit({
      empCode: selectedEmp.empCode, name: selectedEmp.name, type,
      fromDate, toDate, days, reason: reason.trim(),
    });
    setReason("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Apply for Leave</DialogTitle>
          <DialogDescription>Submit a new leave application for approval.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Employee</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.empCode} — {e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Leave Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as LeaveRecord["type"])}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Casual">Casual</SelectItem>
                <SelectItem value="Sick">Sick</SelectItem>
                <SelectItem value="Earned">Earned</SelectItem>
                <SelectItem value="Without Pay">Without Pay</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lv-from">From Date</Label>
              <Input id="lv-from" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="lv-to">To Date</Label>
              <Input id="lv-to" type="date" value={toDate} onChange={e => setToDate(e.target.value)} required />
            </div>
          </div>
          <div className="rounded-md border bg-muted/30 p-2 text-sm">
            Total days: <span className="font-semibold">{days}</span>
          </div>
          <div>
            <Label htmlFor="lv-reason">Reason</Label>
            <Textarea id="lv-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="Brief reason for leave..." required rows={3} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit"><Plus className="size-4 mr-1" /> Submit Application</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===== New Appraisal Dialog =====
function NewAppraisalDialog({
  open, onOpenChange, employees, onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employees: Employee[];
  onSubmit: (data: Omit<AppraisalRecord, "id" | "status">) => void;
}) {
  const [empId, setEmpId] = React.useState(employees[0]?.id ?? "");
  const [period, setPeriod] = React.useState("Jul-Dec 2026");
  const [reviewer, setReviewer] = React.useState("");
  const [goals, setGoals] = React.useState("");

  const selectedEmp = employees.find(e => e.id === empId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmp || !reviewer.trim() || !goals.trim()) return;
    onSubmit({
      empCode: selectedEmp.empCode, name: selectedEmp.name,
      period, rating: "Pending", score: 0, reviewer: reviewer.trim(), goals: goals.trim(),
    });
    setReviewer(""); setGoals("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>New Performance Appraisal</DialogTitle>
          <DialogDescription>Initiate a new appraisal cycle. Reviewer will set rating & score later.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Employee</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.empCode} — {e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ap-period">Period</Label>
              <Input id="ap-period" value={period} onChange={e => setPeriod(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="ap-reviewer">Reviewer</Label>
              <Input id="ap-reviewer" value={reviewer} onChange={e => setReviewer(e.target.value)} placeholder="e.g. CEO / CFO" required />
            </div>
          </div>
          <div>
            <Label htmlFor="ap-goals">Goals / Focus Areas</Label>
            <Textarea id="ap-goals" value={goals} onChange={e => setGoals(e.target.value)} placeholder="e.g. Achieve 110% of revenue target..." required rows={3} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit"><Star className="size-4 mr-1" /> Create Appraisal</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
